import { ReactNode, useState } from 'react'
import { Button, Stack, Typography } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { com } from '@eidu/web'
import PageLayout from '../shared/PageLayout'
import { KindViewPageProps } from '../KindPages'
import useHasPermission from '../../../permission/useHasPermission'
import { getResourceKeyFromEntity } from '../../../../io/entityResourceKeyLink'
import useResource from '../../../../io/useResource'
import useRuntimeEntity from '../../../../io/useRuntimeEntity'
import { AssetSourceView } from './AssetSourceView'
import { AssetPreviewView } from './AssetPreviewView'
import ScrollFadeContainer from '../../../../components/layout/ScrollFadeContainer'
import PersistentTabs, { PersistentTab } from '../../../../components/layout/PersistentTabs'
import { getKnownKindKey } from '../../../../domain/kindRegistry'
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import EntityFieldDisplayGroup from '../shared/EntityFieldDisplayGroup'
import useIsPublishable from './publish/useIsPublishable'
import PublishDialog from './publish/PublishDialog'
import VersionHistoryDrawer from './publish/VersionHistoryDrawer'
import usePublishStatus from './publish/usePublishStatus'
import PublishStatusChip from './publish/PublishStatusChip'

type AssetViewTab = 'preview' | 'source' | 'fields'

export type RenderSourceArgs = { content: string | undefined; isLoading: boolean }

const AssetViewPage = ({
  entity,
  entityType,
  entityTypes,
  fields,
  renderSource,
}: KindViewPageProps & { renderSource?: (args: RenderSourceArgs) => ReactNode }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const pinnedVersionId = searchParams.get('versionId') ?? undefined
  const initialDrawerOpen = searchParams.get('versionHistory') === '1'
  const initialCompareVersionId = searchParams.get('compareVersion') ?? undefined

  const { hasPermission: hasUpdatePermission } = useHasPermission(entity.entity.id, EntityAction.UpdateEntity)
  const { publishable } = useIsPublishable(entity, entityType)

  const navigateToEdit = () => {
    navigate(`/entities/${entityType.id.toString()}/edit/${entity.entity.id.toString()}`)
  }

  const currentResourceKey = getResourceKeyFromEntity(entity)
  const { content, isLoading } = useResource(currentResourceKey)
  const { runtimeEntityId } = useRuntimeEntity(entity)
  const hasRuntime = !!runtimeEntityId

  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const publishStatus = usePublishStatus({ entity })

  return (
    <PageLayout
      title={entity.primaryLabelText}
      subtitle={entityType.name}
      auditTargetId={entity.entity.id.toString()}
      actions={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          {publishable && (
            <>
              <PublishStatusChip
                status={publishStatus}
                entityType={entityType}
                currentResourceKey={currentResourceKey}
              />
              <PublishDialog entityId={entity.entity.id} />
              <VersionHistoryDrawer
                entityId={entity.entity.id}
                initialOpen={initialDrawerOpen}
                initialCompareVersionId={initialCompareVersionId}
              />
            </>
          )}
          {hasUpdatePermission && (
            <Button onClick={navigateToEdit} data-testid="entity-action-edit">
              Edit
            </Button>
          )}
        </Stack>
      }
      showBack
    >
      <Stack sx={{ flex: 1, minHeight: 0 }}>
        <PersistentTabs<AssetViewTab> defaultValue={hasRuntime ? 'preview' : 'source'} sx={{ mb: 1, flexShrink: 0 }}>
          {hasRuntime && (
            <PersistentTab<AssetViewTab> label="Preview" value="preview">
              <AssetPreviewView
                entity={entity}
                entityKind={getKnownKindKey(entityType.kind)}
                entityTypes={entityTypes}
                pinnedVersionId={pinnedVersionId}
                onValidationChange={(result) => setValidationErrors(result.valid ? [] : result.errors)}
              />
              {validationErrors.map((error) => (
                <Typography key={error} variant="caption" color="error">
                  {error}
                </Typography>
              ))}
            </PersistentTab>
          )}
          <PersistentTab<AssetViewTab> label="Source" value="source">
            {renderSource ? (
              renderSource({ content, isLoading })
            ) : (
              <AssetSourceView content={content} isLoading={isLoading} />
            )}
          </PersistentTab>
          <PersistentTab<AssetViewTab> label="Fields" value="fields">
            <ScrollFadeContainer>
              <EntityFieldDisplayGroup entityType={entityType} fields={fields} />
            </ScrollFadeContainer>
          </PersistentTab>
        </PersistentTabs>
      </Stack>
    </PageLayout>
  )
}

export default AssetViewPage
